import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "../redux";
import { loginUser, clearError } from "../../store/slices/authSlice";
import { loginSchema } from "../../schemas/authSchemas";
import { STORAGE_KEYS, APP_ROUTES } from "../../constants";

export const useLoginForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loading,
    error: authError,
  } = useAppSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const passwordRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: {
      errors: validationErrors,
      isDirty: formInteracted,
      isSubmitting: isSubmittingForm,
    },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      instanceName: "",
      rememberMe: false,
    },
    mode: "onChange",
  });

  const emailValue = watch("email");
  const passwordValue = watch("password");
  const instanceNameValue = watch("instanceName");
  const formHasValues =
    !!emailValue.trim() ||
    !!passwordValue.trim() ||
    (instanceNameValue && !!instanceNameValue.trim());

  useEffect(() => {
    const rememberedEmail = localStorage.getItem(STORAGE_KEYS.REMEMBER_EMAIL);
    if (rememberedEmail) {
      setValue("email", rememberedEmail);
      setValue("rememberMe", true);
      setTimeout(() => passwordRef.current?.focus(), 100);
    } else {
      setTimeout(() => document.getElementById("email")?.focus(), 100);
    }

    return () => {
      dispatch(clearError());
    };
  }, [dispatch, setValue]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(APP_ROUTES.CHAT, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        document.activeElement?.blur();
        setFocusedField(null);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, []);

  const onSubmit = async (data) => {
    await dispatch(
      loginUser({
        email: data.email,
        password: data.password,
        instance_name: data.instanceName || undefined,
      })
    );

    if (data.rememberMe) {
      localStorage.setItem(STORAGE_KEYS.REMEMBER_EMAIL, data.email);
    } else {
      localStorage.removeItem(STORAGE_KEYS.REMEMBER_EMAIL);
    }
  };

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" && emailValue && !validationErrors.email) {
      e.preventDefault();
      passwordRef.current?.focus();
    }
  };

  return {
    register,
    handleSubmit,
    onSubmit,
    togglePasswordVisibility,
    handleEmailKeyDown,
    focusedField,
    setFocusedField,
    showPassword,
    validationErrors,
    emailValue,
    formInteracted,
    isSubmittingForm,
    loading,
    formHasValues,
    authError,
    passwordRef,
    instanceNameValue,
  };
};
